// ============================================================
// THE DISPLAY'S GRAMMAR - how terrain is drawn: by material, as pieces.
//
// The display draws terrain as a hololith does (the user's ruling, 2026-10-07):
// translucent faces, lit edges, the edges it cannot see dashed, no shadows,
// no reflections. Two questions live here:
//
//   - HOW a face looks, from its material's transparency (opaque,
//     translucent, transparent) or as a wireframe when it stands between
//     the eye and someone who matters. No colours: the renderer pairs these
//     numbers with its tokens.
//   - WHICH edges to draw. Terrain is meshed into many solids (voxels are
//     greedy-meshed into boxes), and drawing every solid's edges showed the
//     user "a collection of stacked planes". Here faces of one material that
//     share a plane are one surface: an edge two of them share is a seam and
//     is dropped, and a face buried against another solid's opposite face
//     is dropped whole. This is drawing only: the meshed solids that cover
//     and sight read are untouched.
//
// Approximate where it says so (a buried face must lie inside the other's
// box; a partly buried face keeps its edges). Pure. Zero React dependency.
// ============================================================
import type { FaceKind, Solid, Vec3 } from '../battlefield/types';
import { solidGeometry, dot, sub, boundingBox, solidTransparency } from '../battlefield/solids';
import type { Box } from '../battlefield/solids';
import { isFrontFacing } from '../battlefield/scene';

export type Treatment = 'opaque' | 'translucent' | 'transparent';

export interface FaceLook {
  /** Fill opacity, 0 for none. */
  fill: number;
  /** Edge opacity. */
  stroke: number;
  /** Edge width in px at zoom 1 (the renderer scales nothing by it). */
  width: number;
  /** An SVG dash pattern, or null for a solid line. */
  dash: string | null;
}

/** The treatment for a material's transparency. */
export function treatmentOf(transparency: 0 | 1 | 2): Treatment {
  return (['opaque', 'translucent', 'transparent'] as const)[transparency];
}

const LOOKS: Record<Treatment, { top: FaceLook; side: FaceLook }> = {
  opaque: {
    top: { fill: 0.2, stroke: 0.85, width: 1.2, dash: null },
    side: { fill: 0.1, stroke: 0.85, width: 1, dash: null },
  },
  translucent: {
    top: { fill: 0.08, stroke: 0.7, width: 1, dash: '2 3' },
    side: { fill: 0.05, stroke: 0.7, width: 1, dash: '2 3' },
  },
  transparent: {
    top: { fill: 0, stroke: 0.8, width: 0.8, dash: null },
    side: { fill: 0, stroke: 0.8, width: 0.8, dash: null },
  },
};

/** How a face of this kind is drawn under this treatment. */
export function faceLook(kind: FaceKind, treatment: Treatment): FaceLook {
  return kind === 'top' ? LOOKS[treatment].top : LOOKS[treatment].side;
}

// ---- the outline: one piece per surface ----

export interface OutlineEdge {
  a: Vec3;
  b: Vec3;
  /** Turned towards the eye: drawn lit. The rest are drawn dashed and faint. */
  front: boolean;
  /** The material's own colour, the edge's tint; absent for the display's default. */
  colour?: string;
  /** How its material is drawn: the renderer takes its stroke, width and dash from `faceLook`. */
  treatment: Treatment;
}

interface WorldFace {
  normal: Vec3;
  offset: number;
  points: Vec3[];
  material: string;
  treatment: Treatment;
  colour?: string;
}

const EPS = 1e-4;
const r = (n: number) => (Math.abs(n) < EPS ? 0 : n).toFixed(4);
const pointKey = (p: Vec3) => `${r(p.x)},${r(p.y)},${r(p.z)}`;
const edgeKey = (a: Vec3, b: Vec3) => [pointKey(a), pointKey(b)].sort().join('|');
/** A plane, by its outward normal and offset: the faces that lie in it. */
const sideKey = (normal: Vec3, offset: number) => `${r(normal.x)},${r(normal.y)},${r(normal.z)}|${r(offset)}`;
/** A surface: one plane, one material. */
const planeKey = (f: WorldFace) => `${sideKey(f.normal, f.offset)}|${f.material}`;

/** Append to a map of lists. */
function pushTo<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

/** One piece's identity: faces of different materials never merge. */
function materialOf(solid: Solid): string {
  return `${solidTransparency(solid)}|${solid.colour ?? ''}|${solid.hidden ? 'h' : ''}`;
}

function worldFaces(solid: Solid): WorldFace[] {
  const g = solidGeometry(solid);
  const material = materialOf(solid);
  const treatment = treatmentOf(solidTransparency(solid));
  return g.faces.map((f) => {
    const points = f.indices.map((i) => g.vertices[i]);
    return {
      normal: f.normal,
      offset: dot(f.normal, points[0]),
      points,
      material,
      treatment,
      ...(solid.colour ? { colour: solid.colour } : {}),
    };
  });
}

const within = (p: Vec3, b: Box) =>
  p.x >= b.min.x - EPS &&
  p.x <= b.max.x + EPS &&
  p.y >= b.min.y - EPS &&
  p.y <= b.max.y + EPS &&
  p.z >= b.min.z - EPS &&
  p.z <= b.max.z + EPS;

/**
 * The faces pressed flat against an opposite face that covers them: inside the
 * piece. Faces are indexed by plane, so each looks only at the faces in the
 * plane it would be pressed against (the budget test caught the all-pairs
 * version at 400 ms on a field of ruins).
 */
function buriedFaces(all: WorldFace[]): Set<WorldFace> {
  const byPlane = new Map<string, { face: WorldFace; box: Box }[]>();
  for (const face of all)
    pushTo(byPlane, sideKey(face.normal, face.offset), { face, box: boundingBox({ vertices: face.points }) });
  const out = new Set<WorldFace>();
  for (const face of all) {
    const flipped = { x: -face.normal.x, y: -face.normal.y, z: -face.normal.z };
    const against = byPlane.get(sideKey(flipped, -face.offset)) ?? [];
    if (against.some((o) => o.face !== face && face.points.every((p) => within(p, o.box)))) out.add(face);
  }
  return out;
}

/**
 * Which vertices could lie on an edge: for an edge along an axis (every
 * voxel edge), only those on the same axis line; for any other, all of them.
 */
type LineIndex = { lines: Map<string, Vec3[]>; all: Vec3[] };

function axisLine(a: Vec3, b: Vec3): string | null {
  if (Math.abs(a.y - b.y) < EPS && Math.abs(a.z - b.z) < EPS) return `x|${r(a.y)},${r(a.z)}`;
  if (Math.abs(a.x - b.x) < EPS && Math.abs(a.z - b.z) < EPS) return `y|${r(a.x)},${r(a.z)}`;
  if (Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS) return `z|${r(a.x)},${r(a.y)}`;
  return null;
}

function lineIndex(vertices: Vec3[]): LineIndex {
  const lines = new Map<string, Vec3[]>();
  for (const p of vertices) {
    pushTo(lines, `x|${r(p.y)},${r(p.z)}`, p);
    pushTo(lines, `y|${r(p.x)},${r(p.z)}`, p);
    pushTo(lines, `z|${r(p.x)},${r(p.y)}`, p);
  }
  return { lines, all: vertices };
}

const candidates = (a: Vec3, b: Vec3, index: LineIndex) => {
  const k = axisLine(a, b);
  return k ? (index.lines.get(k) ?? []) : index.all;
};

/** Split an edge wherever another vertex of its surface lies on it, so partial seams can cancel. */
function splitEdge(a: Vec3, b: Vec3, index: LineIndex): [Vec3, Vec3][] {
  const vertices = candidates(a, b, index);
  const d = sub(b, a);
  const len2 = dot(d, d);
  const cuts = vertices
    .map((p) => ({ p, t: dot(sub(p, a), d) / len2 }))
    .filter(({ p, t }) => t > EPS && t < 1 - EPS && onLine(a, d, t, p))
    .sort((u, v) => u.t - v.t)
    .map(({ p }) => p);
  const stops = [a, ...cuts, b];
  return stops.slice(1).map((p, i) => [stops[i], p] as [Vec3, Vec3]);
}

const onLine = (a: Vec3, d: Vec3, t: number, p: Vec3) =>
  Math.abs(a.x + d.x * t - p.x) < EPS && Math.abs(a.y + d.y * t - p.y) < EPS && Math.abs(a.z + d.z * t - p.z) < EPS;

type Segment = { a: Vec3; b: Vec3; key: string };

/** A surface's edges that are not seams: those only one of its faces has, each with its key. */
function surfaceOutline(faces: WorldFace[]): Segment[] {
  const index = lineIndex(faces.flatMap((f) => f.points));
  const counted = new Map<string, { seg: Segment; n: number }>();
  for (const f of faces) {
    f.points.forEach((a, i) => {
      for (const [p, q] of splitEdge(a, f.points[(i + 1) % f.points.length], index)) {
        const key = edgeKey(p, q);
        const hit = counted.get(key);
        counted.set(key, { seg: hit?.seg ?? { a: p, b: q, key }, n: (hit?.n ?? 0) + 1 });
      }
    });
  }
  return [...counted.values()].filter((e) => e.n === 1).map((e) => e.seg);
}

/** What an edge inherits from its material: its tint and how it is drawn. */
interface MaterialLook {
  treatment: Treatment;
  colour?: string;
}

/** A surface of one material: its outward normal and the edges of its outline. */
interface Surface extends MaterialLook {
  normal: Vec3;
  /** Each edge with its key, made once here so a camera turn formats nothing. */
  segments: Segment[];
}

/** A seam between two facets of a round solid: drawn only where it is the silhouette. */
interface Seam extends MaterialLook {
  a: Vec3;
  b: Vec3;
  key: string;
  normals: [Vec3, Vec3];
}

/** What the display draws of the terrain: flat surfaces' outlines, and round solids' seams. */
export interface Outline {
  surfaces: Surface[];
  seams: Seam[];
}

/** Facets meeting at less than this are one curved surface, not a corner. */
const SMOOTH_COS = Math.cos((35 * Math.PI) / 180);

/** The vertical seams between neighbouring side facets of one round solid. */
function smoothSeams(solid: Solid): Seam[] {
  const g = solidGeometry(solid);
  const sides = g.faces.filter((f) => f.kind === 'side');
  return sides.flatMap((f, i) => {
    const next = sides[(i + 1) % sides.length];
    if (sides.length < 5 || dot(f.normal, next.normal) < SMOOTH_COS) return [];
    const shared = f.indices.filter((v) => next.indices.includes(v)).map((v) => g.vertices[v]);
    if (shared.length !== 2) return [];
    return [
      {
        a: shared[0],
        b: shared[1],
        key: edgeKey(shared[0], shared[1]),
        normals: [f.normal, next.normal] as [Vec3, Vec3],
        treatment: treatmentOf(solidTransparency(solid)),
        ...(solid.colour ? { colour: solid.colour } : {}),
      },
    ];
  });
}

/**
 * What these solids make, seams between surfaces and buried faces dropped,
 * and a round solid's facet seams set apart. Depends on the terrain only, so
 * a renderer computes it once per terrain change, not on every camera turn.
 */
export function outlineSurfaces(solids: readonly Solid[]): Outline {
  const seams = solids.flatMap(smoothSeams);
  const smooth = new Set(seams.map((s) => s.key));
  const all = solids.flatMap(worldFaces);
  const buried = buriedFaces(all);
  const groups = new Map<string, WorldFace[]>();
  for (const f of all) if (!buried.has(f)) pushTo(groups, planeKey(f), f);
  const surfaces = [...groups.values()].map((faces) => ({
    normal: faces[0].normal,
    treatment: faces[0].treatment,
    ...(faces[0].colour ? { colour: faces[0].colour } : {}),
    segments: surfaceOutline(faces).filter((seg) => !smooth.has(seg.key)),
  }));
  return { surfaces, seams };
}

/**
 * Every edge to draw, each once: lit when a surface it bounds turns to the
 * eye. `sight` gives the line of sight into the screen at a point
 * (`sightTo`: from the eye under perspective). Cheap: this is the part that
 * changes as the camera turns.
 */
export function edgesFacing(outline: Outline, sight: (p: Vec3) => Vec3): OutlineEdge[] {
  const edges = new Map<string, OutlineEdge>();
  for (const s of outline.seams) {
    // A curved surface's edge is where it turns away from the eye: one facet towards, its neighbour not.
    const at = sight(s.a);
    if (isFrontFacing(s.normals[0], at) !== isFrontFacing(s.normals[1], at)) addEdge(edges, s, true, s);
  }
  for (const s of outline.surfaces) addSurface(edges, s, sight);
  return [...edges.values()];
}

/** A flat surface's edges, lit or not as a whole: it faces the eye the same way at every point, so its first edge will do. */
function addSurface(edges: Map<string, OutlineEdge>, s: Outline['surfaces'][number], sight: (p: Vec3) => Vec3) {
  if (!s.segments.length) return;
  const front = isFrontFacing(s.normal, sight(s.segments[0].a));
  for (const seg of s.segments) addEdge(edges, seg, front, s);
}

/**
 * Add an edge once, by its precomputed key: lit if any surface it bounds is.
 * Where two materials meet, the first surface to claim the edge gives it its
 * look, and a colour from either fills an untinted one.
 */
function addEdge(edges: Map<string, OutlineEdge>, seg: Segment, front: boolean, look: MaterialLook) {
  const had = edges.get(seg.key);
  if (!had) {
    edges.set(seg.key, {
      a: seg.a,
      b: seg.b,
      front,
      treatment: look.treatment,
      ...(look.colour ? { colour: look.colour } : {}),
    });
    return;
  }
  if (front) had.front = true;
  if (look.colour && !had.colour) had.colour = look.colour;
}
